import React, { useState, useEffect, useCallback } from 'react';
import { Search, MoreVertical, UserCheck, UserX, Shield, ShieldOff, Trash2, Key } from 'lucide-react';
import { userAPI } from '../../services/api';

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ role: '', isVerified: '', isSuspended: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionMenu, setActionMenu] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, search, ...filters };
      const response = await userAPI.getUsers(params);

      if (response.data.success) {
        setUsers(response.data.data);
        setTotalPages(response.data.pages);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, filters]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleAction = async (action, userId) => {
    try {
      let response;
      switch (action) {
        case 'suspend':
          const reason = prompt('Enter suspension reason:');
          if (reason) {
            response = await userAPI.suspendUser(userId, reason);
          }
          break;
        case 'unsuspend':
          response = await userAPI.unsuspendUser(userId);
          break;
        case 'verify':
          response = await userAPI.verifyUser(userId);
          break;
        case 'makeAdmin':
          if (window.confirm('Grant admin privileges to this user?')) {
            response = await userAPI.makeAdmin(userId);
          }
          break;
        case 'removeAdmin':
          if (window.confirm('Remove admin privileges from this user?')) {
            response = await userAPI.removeAdmin(userId);
          }
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            response = await userAPI.deleteUser(userId);
          }
          break;
        case 'resetPassword':
          const newPassword = prompt('Enter new password (min 6 characters):');
          if (newPassword && newPassword.length >= 6) {
            response = await userAPI.adminResetPassword(userId, newPassword);
            if (response?.data.success) {
              alert('Password reset successfully!');
            }
          } else if (newPassword) {
            alert('Password must be at least 6 characters');
          }
          break;
        default:
          break;
      }

      if (response?.data.success) {
        loadUsers();
        setActionMenu(null);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed');
    }
  };

  return (
    <div>
      <h1 style={{ marginBottom: '24px' }}>Users Management</h1>

      {/* Search and Filters */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
          <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--textSecondary)' }} />
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 42px',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          />
        </div>

        <select
          value={filters.isVerified}
          onChange={(e) => setFilters({ ...filters, isVerified: e.target.value })}
          style={{
            padding: '12px',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          <option value="">All Verification Status</option>
          <option value="true">Verified</option>
          <option value="false">Unverified</option>
        </select>

        <select
          value={filters.isSuspended}
          onChange={(e) => setFilters({ ...filters, isSuspended: e.target.value })}
          style={{
            padding: '12px',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          <option value="">All Status</option>
          <option value="false">Active</option>
          <option value="true">Suspended</option>
        </select>

        <select
          value={filters.role}
          onChange={(e) => setFilters({ ...filters, role: e.target.value })}
          style={{
            padding: '12px',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          <option value="">All Roles</option>
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      {/* Users Table */}
      <div style={{ background: 'var(--cardBackground)', borderRadius: '8px', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--background)', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>User</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Email</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Role</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Joined</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--textSecondary)' }}>
                    Loading users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--textSecondary)' }}>
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user._id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: user.profileImage ? 'transparent' : 'var(--primary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          overflow: 'hidden',
                        }}>
                          {user.profileImage ? (
                            <img src={user.profileImage} alt={user.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            user.username[0].toUpperCase()
                          )}
                        </div>
                        <div>
                          <div style={{ fontWeight: '500' }}>{user.username}</div>
                          {user.isVerified && <UserCheck size={14} style={{ color: 'var(--success)' }} />}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>{user.email}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        background: user.isAdmin ? 'var(--primary)' : 'var(--secondary)',
                        color: user.isAdmin ? 'white' : 'var(--text)',
                      }}>
                        {user.role}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        background: user.isSuspended ? 'var(--error)' : 'var(--success)',
                        color: 'white',
                      }}>
                        {user.isSuspended ? 'Suspended' : 'Active'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: 'var(--textSecondary)', fontSize: '14px' }}>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <div style={{ position: 'relative' }}>
                        <button
                          onClick={() => setActionMenu(actionMenu === user._id ? null : user._id)}
                          style={{
                            padding: '8px',
                            background: 'transparent',
                            border: '1px solid var(--border)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                          }}
                        >
                          <MoreVertical size={16} />
                        </button>

                        {actionMenu === user._id && (
                          <div style={{
                            position: 'absolute',
                            right: 0,
                            top: '100%',
                            marginTop: '4px',
                            background: 'var(--cardBackground)',
                            border: '1px solid var(--border)',
                            borderRadius: '6px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            zIndex: 10,
                            minWidth: '180px',
                          }}>
                            <button
                              onClick={() => handleAction('verify', user._id)}
                              disabled={user.isVerified}
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                textAlign: 'left',
                                background: 'transparent',
                                border: 'none',
                                cursor: user.isVerified ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: user.isVerified ? 0.5 : 1,
                              }}
                            >
                              <UserCheck size={16} />
                              Verify User
                            </button>

                            {user.isSuspended ? (
                              <button
                                onClick={() => handleAction('unsuspend', user._id)}
                                style={{
                                  width: '100%',
                                  padding: '10px 16px',
                                  textAlign: 'left',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <UserCheck size={16} />
                                Unsuspend
                              </button>
                            ) : (
                              <button
                                onClick={() => handleAction('suspend', user._id)}
                                style={{
                                  width: '100%',
                                  padding: '10px 16px',
                                  textAlign: 'left',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <UserX size={16} />
                                Suspend
                              </button>
                            )}

                            {user.isAdmin ? (
                              <button
                                onClick={() => handleAction('removeAdmin', user._id)}
                                style={{
                                  width: '100%',
                                  padding: '10px 16px',
                                  textAlign: 'left',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <ShieldOff size={16} />
                                Remove Admin
                              </button>
                            ) : (
                              <button
                                onClick={() => handleAction('makeAdmin', user._id)}
                                style={{
                                  width: '100%',
                                  padding: '10px 16px',
                                  textAlign: 'left',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <Shield size={16} />
                                Make Admin
                              </button>
                            )}

                            <button
                              onClick={() => handleAction('resetPassword', user._id)}
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                textAlign: 'left',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: 'var(--text)',
                              }}
                            >
                              <Key size={16} />
                              Reset Password
                            </button>

                            <button
                              onClick={() => handleAction('delete', user._id)}
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                textAlign: 'left',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: 'var(--error)',
                                borderTop: '1px solid var(--border)',
                              }}
                            >
                              <Trash2 size={16} />
                              Delete User
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div style={{ padding: '16px', display: 'flex', justifyContent: 'center', gap: '8px', borderTop: '1px solid var(--border)' }}>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              style={{
                padding: '8px 16px',
                background: page === 1 ? 'var(--secondary)' : 'var(--primary)',
                color: page === 1 ? 'var(--textSecondary)' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              Previous
            </button>
            <span style={{ padding: '8px 16px', display: 'flex', alignItems: 'center' }}>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              style={{
                padding: '8px 16px',
                background: page === totalPages ? 'var(--secondary)' : 'var(--primary)',
                color: page === totalPages ? 'var(--textSecondary)' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: page === totalPages ? 'not-allowed' : 'pointer',
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersTab;
